/* eslint-disable no-console */
import { io, type Socket } from "socket.io-client";

/**
 * Parse a backend `UaFunctionValueMessage` into the shape our handlers
 * historically expected. The wire format only carries
 * `{ browsePath, data }`; consumers want `deviceId`, `unitId`, `functionName`,
 * `variableType` for matching/filtering. Derive them from the browse path:
 *
 *   "DeviceSet/<deviceId>/FunctionalUnitSet/<unitId>/FunctionSet/<funcId>/<variableType>"
 *   "DeviceSet/<deviceId>/FunctionalUnitSet/<unitId>/FunctionalUnitState/<variableType>"
 *
 * `unitId` is load-bearing for cross-device "organized" FU mappings: when a
 * source FU is mounted under a target device (e.g. FreezerUnit imported into
 * myViscometer), the (deviceId, functionName) tuple alone collides with the
 * target device's own same-named functions. Without `unitId`, the imported
 * FU's TemperatureController/CurrentValue overwrites the host's
 * ViscometerUnit/TemperatureController/CurrentValue slot.
 *
 * Falls back to fields that may already be present on `raw` (forward-compat
 * if the backend wire format ever ships them directly).
 */
export function enrichValueMessage(
  raw: { browsePath?: string; data?: unknown } & Partial<UAFunctionValueMessage>
): UAFunctionValueMessage {
  const browsePath = raw.browsePath ?? "";
  const parts = browsePath.split("/").filter(Boolean);
  const idxOf = (segment: string) => parts.indexOf(segment);

  const deviceIdx = idxOf("DeviceSet");
  const fuSetIdx = idxOf("FunctionalUnitSet");
  const fnSetIdx = idxOf("FunctionSet");
  const fuStateIdx = idxOf("FunctionalUnitState");

  const deviceId =
    (deviceIdx >= 0 && parts[deviceIdx + 1]) || raw.deviceId || "";

  const unitId = (fuSetIdx >= 0 && parts[fuSetIdx + 1]) || raw.unitId || "";
  let functionName = raw.functionName ?? "";
  let variableType = raw.variableType ?? "";
  if (fnSetIdx >= 0 && fnSetIdx + 1 < parts.length) {
    // Everything between the first FunctionSet marker and the trailing
    // variable segment is the (possibly nested) function path. Joining the
    // whole span — instead of taking only the first segment — keeps the
    // (deviceId, unitId, functionName, variableType) tuple unique for nested
    // functions; otherwise two sub-functions of the same parent collide and
    // overwrite each other. Literal "FunctionSet" markers are dropped so the
    // name reads like "Func1/SubFunc" (flat case stays just "Func1").
    variableType = parts.at(-1) ?? "";
    functionName = parts
      .slice(fnSetIdx + 1, -1)
      .filter((p) => p !== "FunctionSet")
      .join("/");
  } else if (fuStateIdx >= 0) {
    functionName = "FunctionalUnitState";
    variableType = parts.at(-1) ?? "";
  } else if (!variableType && parts.length > 0) {
    variableType = parts.at(-1) ?? "";
  }

  return {
    browsePath,
    deviceId,
    unitId,
    functionName,
    variableType,
    data: (raw.data ?? { value: undefined }) as UAFunctionValueMessage["data"],
  };
}

/** Function-relative path from the FunctionSet marker on, e.g.
 *  "FunctionSet/Func1" or nested "FunctionSet/Composite/FunctionSet/SubA". */
function functionSuffix(path?: string): string | undefined {
  const i = path?.indexOf("/FunctionSet/") ?? -1;
  return i >= 0 ? path!.slice(i + 1) : undefined;
}

/**
 * Match a value-update browsePath to a channel key by canonical browsePath.
 * Strips the trailing variable segment, then compares from the FunctionSet
 * segment on — so an organized cross-device FU (emitted under the mount/target
 * path) still matches its source-side channel, and nested sub-functions stay
 * uniquely addressable. Returns the channel key, or undefined if none match.
 */
export function matchChannelByBrowsePath(
  channels: Record<string, { browsePath?: string } | undefined>,
  variableBrowsePath?: string
): string | undefined {
  const functionPath = variableBrowsePath?.split("/").slice(0, -1).join("/");
  if (!functionPath) return undefined;
  const keys = Object.keys(channels);
  // Exact canonical match first — suffix-only would collide when two mounted
  // functions from different source devices share a browseName (LCU-875).
  const exact = keys.find((key) => channels[key]?.browsePath === functionPath);
  if (exact) return exact;
  const target = functionSuffix(functionPath);
  if (!target) return undefined;
  return keys.find(
    (key) => functionSuffix(channels[key]?.browsePath) === target
  );
}

/**
 * LADS WebSocket Events (Room-based subscriptions)
 *
 * Subscribe to a room (hierarchicalLocation) to receive:
 *
 * 1. UAFunctionValue - Live sensor/function values
 *    - Emitted when a function value changes (SensorValue, CurrentValue, TargetValue)
 *    - Contains namespace, nodeId, deviceId, functionName, variableType and value data
 *
 * 2. DeviceChanged - Device state changes
 *    - status: Device went online/offline
 *    - added: New device discovered
 *    - removed: Device removed from system
 *    - metadata: Device properties changed (name, location, etc.)
 */

// Value update message for live sensor data
export type UAFunctionValueMessage = {
  /** Canonical browse path emitted by the backend gateway, e.g.
   *  `DeviceSet/<deviceId>/FunctionalUnitSet/<unitId>/FunctionSet/<funcId>/SensorValue`. */
  browsePath?: string;
  deviceId: string;
  /** FunctionalUnit segment — required to distinguish a host-device's own
   *  FU from an organized cross-device FU mounted under the same device. */
  unitId: string;
  functionName: string;
  variableType: string;
  data: {
    value: any;
    serverTimestamp?: string;
    sourceTimestamp?: string;
    statusCode?: string;
  };
};

// Metadata message for units, precision, and alarm limits
export type UAFunctionMetadataMessage = {
  deviceId: string;
  functionName: string;
  metadata: {
    unit?: string;
    unitDescription?: string;
    euRange?: { low: number; high: number };
    valuePrecision: number;
    alarmMonitor?: {
      highHighLimit?: number;
      highLimit?: number;
      lowLimit?: number;
      lowLowLimit?: number;
      userAccessLevel?: string[];
    };
  };
};

// Resolved attribute values carried by a UASemanticChangeEvent.
export type DeviceSemanticData = {
  componentName?: string;
  hierarchicalLocation?: string;
  operationalLocation?: string;
  assetId?: string;
};

// Structural change verb, derived from UAGeneralModelChangeEvent.
export type DeviceModelChange = "added" | "removed";

// Alarm condition transition. Receiver typically calls `mutate()` on the SWR
// cache for /incidents — the payload is only enough to decide whether the
// refetch is relevant to the current view.
export type IncidentChangedMessage = {
  browsePath: string;
  active: boolean;
  limitState?: string;
  severity?: number;
  auditAction?: "ack" | "confirm" | "report";
  /** OPC-UA EventId (base64) — identifies the incident for follow-up
   *  operator actions (ack/confirm) without a refetch. */
  incidentId?: string;
  /** ISO-8601 timestamp of the event. */
  createdAt?: string;
  /** Operator/system message from the alarm event. */
  message?: string;
  /** Pre-computed by backend: "ERROR"/"ALERT" → critical, others → warning. */
  severityLevel?: "ERROR" | "ALERT" | "WARNING" | "INFO" | "NOTICE";
};

export type WebSocketConfig = {
  url: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
};

type ValueHandler = (data: UAFunctionValueMessage) => void;
type DeviceStateHandler = (deviceId: string, isOnline: boolean) => void;
type ModelChangeHandler = (deviceId: string, change: DeviceModelChange) => void;
type SemanticChangeHandler = (
  deviceId: string,
  data: DeviceSemanticData
) => void;
type MetadataHandler = (data: UAFunctionMetadataMessage) => void;
type IncidentChangedHandler = (data: IncidentChangedMessage) => void;

export class LADSWebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private valueHandlers = new Map<string, ValueHandler>();
  private deviceStateHandlers = new Map<string, DeviceStateHandler>();
  private modelChangeHandlers = new Map<string, ModelChangeHandler>();
  private semanticChangeHandlers = new Map<string, SemanticChangeHandler>();
  private metadataHandlers = new Map<string, MetadataHandler>();
  private incidentChangedHandlers = new Map<string, IncidentChangedHandler>();
  private config: WebSocketConfig;
  private isConnected = false;
  private roomSubscriptions = new Map<string, number>(); // Reference counting for rooms
  /** Bearer token sent on the socket.io handshake. Read fresh on every
   *  (re)connect via the `auth` callback, so token refreshes are picked up. */
  private token: string | null = null;

  /** Cached metadata by "deviceId/functionName" for quick lookup */
  private metadataCache = new Map<
    string,
    UAFunctionMetadataMessage["metadata"]
  >();

  constructor(config: WebSocketConfig) {
    this.config = {
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      ...config,
    };
  }

  /**
   * Feed the current Bearer token to the socket. Called from the auth
   * layer whenever the access token changes. The first non-null token
   * triggers the (re)connect that the token-less auto-connect at import
   * couldn't do yet; later refreshes are picked up on the next reconnect
   * via the `auth` callback, without disrupting a live connection.
   */
  setToken(token: string | null): void {
    this.token = token;
    if (token && !this.isConnected) {
      this.connect().catch((error) => {
        console.error("[WS] connect after token update failed:", error);
      });
    }
  }

  connect(): Promise<void> {
    // In Storybook/Chromatic gibt es kein Backend. Keine echte Verbindung
    // aufbauen — sonst pollt socket.io ins Leere (404) und Reconnect-Schleifen
    // laufen während der Snapshots. import.meta.env.STORYBOOK wird im
    // Storybook-Build gesetzt (.storybook/main.ts), im App-Build ist es
    // undefined → normaler Verbindungsaufbau.
    if ((import.meta.env as { STORYBOOK?: boolean }).STORYBOOK) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      try {
        const url = new URL(this.config.url);
        // Use pathname from URL as socket.io path prefix (e.g. "/api/v2/lads" → "/api/v2/lads/socket.io")
        const basePath = url.pathname.replace(/\/+$/, "");
        const socketPath = basePath ? `${basePath}/socket.io` : "/socket.io";

        // Tear down a prior socket (e.g. the token-less auto-connect at
        // import) before opening a new one, so we never run two in parallel.
        if (this.socket) {
          this.socket.removeAllListeners();
          this.socket.disconnect();
          this.socket = null;
        }
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }

        this.socket = io(url.origin, {
          path: socketPath,
          // Function form: socket.io invokes it on every (re)connect, so the
          // freshest token is sent. Browsers can't set WS headers, hence the
          // `auth` payload (backend reads handshake.auth.token). Empty string
          // when unauthenticated → backend rejects unless OIDC is disabled.
          auth: (cb) => cb({ token: this.token ?? "" }),
        });

        this.socket.on("connect", () => {
          console.log("[WS] Connected to server");
          this.isConnected = true;
          this.reconnectAttempts = 0;
          // Rejoin all rooms after (re)connect
          this.rejoinAllRooms();
          resolve();
        });

        this.socket.on("disconnect", (reason) => {
          console.log("[WS] Disconnected:", reason);
          this.isConnected = false;
          this.handleDisconnect();
        });

        this.socket.on("connect_error", (error) => {
          console.log("[WS] Connection error:", error.message);
          this.isConnected = false;
          reject(error);
        });

        // Value changes (live sensor data).
        //
        // Wire format from the backend (`UaFunctionValueMessage` in
        // lcc-backend) only carries `{ browsePath, data }`. The downstream
        // handlers historically read `deviceId`, `functionName`,
        // `variableType` directly from the message — those weren't there,
        // so every value update was silently dropped (matched `undefined`
        // against the type-whitelist and returned). Derive the three from
        // the canonical browsePath here so all subscribers see consistent
        // fields without each having to parse the path themselves.
        // DataChange family (OPC-UA conform, see asyncapi.yaml). Values and the
        // unit-level state machines share one payload shape ({browsePath,data})
        // and the store router (resolveWsUpdate) already dispatches them by
        // browsePath, so one handler serves all three. The backend now emits
        // FunctionalUnitState / ProgramManager under their own event names
        // (no longer as UAFunctionValue) — listening to just UAFunctionValue
        // would drop every state and program update.
        const handleDataChange = (
          event: string,
          raw: {
            browsePath?: string;
            data?: unknown;
          } & Partial<UAFunctionValueMessage>
        ): void => {
          const enriched = enrichValueMessage(raw);
          console.log(
            `[WS] ${event} ${enriched.browsePath} → device=${
              enriched.deviceId
            } unit=${enriched.unitId} fn=${enriched.functionName} var=${
              enriched.variableType
            } value=${JSON.stringify(enriched.data?.value)}`
          );
          for (const handler of this.valueHandlers.values()) {
            handler(enriched);
          }
        };
        for (const event of [
          "UAFunctionValue",
          "UAFunctionalUnitState",
          "UAProgramState",
        ]) {
          this.socket.on(event, (raw) => handleDataChange(event, raw));
        }

        // Device-level state. Online/offline is the OPC-UA pendant of the
        // DeviceState statusCode (offline → UncertainNoCommunication…), so we
        // derive the badge from statusCode and feed the device reconciler.
        this.socket.on("UADeviceState", (raw) => {
          const enriched = enrichValueMessage(raw);
          const status = enriched.data?.statusCode ?? "";
          const isOnline = !/^(bad|uncertain)/i.test(status);
          console.log(
            `[WS] UADeviceState ${enriched.browsePath} status=${status} → online=${isOnline}`
          );
          for (const handler of this.deviceStateHandlers.values()) {
            handler(enriched.deviceId, isOnline);
          }
        });

        // Per-ControlFunction state → owning channel (resolveWsUpdate routes it).
        this.socket.on("UAControlFunctionState", (raw) =>
          handleDataChange("UAControlFunctionState", raw)
        );

        // OPC-UA GeneralModelChangeEvent — structural change. A device-level
        // NodeDeleted removes the device; any other structural change (function
        // toggled, reference change, NodeAdded) is an "added" → refetch.
        this.socket.on(
          "UAGeneralModelChangeEvent",
          (msg: {
            changes?: Array<{ browsePath?: string; verb?: string }>;
          }) => {
            console.log("[WS] UAGeneralModelChangeEvent received:", msg);
            const seen = new Set<string>();
            for (const change of msg.changes ?? []) {
              const parts = (change.browsePath ?? "")
                .split("/")
                .filter(Boolean);
              const deviceId = parts[1];
              if (!deviceId) continue;
              const isDeviceRemoval =
                change.verb === "NodeDeleted" && parts.length === 2;
              const kind: DeviceModelChange = isDeviceRemoval
                ? "removed"
                : "added";
              const dedup = `${kind}:${deviceId}`;
              if (seen.has(dedup)) continue;
              seen.add(dedup);
              for (const handler of this.modelChangeHandlers.values()) {
                handler(deviceId, kind);
              }
            }
          }
        );

        // OPC-UA SemanticChangeEvent — meaning change (rename/relocate/assetId).
        this.socket.on(
          "UASemanticChangeEvent",
          (msg: { deviceId?: string; data?: DeviceSemanticData }) => {
            console.log("[WS] UASemanticChangeEvent received:", msg);
            if (!msg.deviceId) return;
            for (const handler of this.semanticChangeHandlers.values()) {
              handler(msg.deviceId, msg.data ?? {});
            }
          }
        );

        // Function metadata (units, precision, range)
        this.socket.on(
          "UAFunctionMetadata",
          (data: UAFunctionMetadataMessage) => {
            // Debug: uncomment to trace metadata updates
            // console.log("[WS] UAFunctionMetadata received:", data.namespace, data.nodeId, data.metadata);
            // Cache metadata by deviceId/functionName
            this.metadataCache.set(
              `${data.deviceId}/${data.functionName}`,
              data.metadata
            );
            for (const handler of this.metadataHandlers.values()) {
              handler(data);
            }
          }
        );

        // Alarm condition transitions — replaces the per-second /incidents poll.
        this.socket.on("IncidentChanged", (data: IncidentChangedMessage) => {
          console.log(
            `[WS] IncidentChanged ${data.browsePath} active=${
              data.active
            } audit=${data.auditAction ?? "-"} sev=${
              data.severityLevel ?? data.severity ?? "-"
            } limit=${data.limitState ?? "-"} id=${
              data.incidentId ? data.incidentId.slice(0, 8) : "-"
            }`
          );
          for (const handler of this.incidentChangedHandlers.values()) {
            handler(data);
          }
        });

        this.socket.on("error", (error) => {
          console.error("[WS] Socket error:", error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
  }

  private handleDisconnect(): void {
    if (this.reconnectAttempts < (this.config.maxReconnectAttempts || 10)) {
      this.reconnectAttempts++;

      this.reconnectTimer = setTimeout(() => {
        console.log(
          `[WS] Reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts}`
        );
        this.connect().catch((error) => {
          console.error("[WS] Reconnect failed:", error);
        });
      }, this.config.reconnectInterval || 5000);
    }
  }

  /**
   * Rejoin all subscribed rooms after reconnect
   */
  private rejoinAllRooms(): void {
    if (!this.socket || !this.isConnected) return;

    const rooms = [...this.roomSubscriptions.keys()];
    console.log("[WS] Rejoining rooms:", rooms);
    for (const roomId of rooms) {
      this.socket.emit("join", roomId);
    }
  }

  /** Register a handler under a fresh UUID; returns its unsubscribe fn. */
  private addHandler<H>(map: Map<string, H>, handler: H): () => void {
    const id = crypto.randomUUID();
    map.set(id, handler);
    return () => map.delete(id);
  }

  /**
   * Subscribe to value changes (UAFunctionValue events)
   * @returns Unsubscribe function
   */
  onValue(handler: ValueHandler): () => void {
    return this.addHandler(this.valueHandlers, handler);
  }

  /** Subscribe to device online/offline (UADeviceState statusCode). */
  onDeviceState(handler: DeviceStateHandler): () => void {
    return this.addHandler(this.deviceStateHandlers, handler);
  }

  /** Subscribe to structural changes (UAGeneralModelChangeEvent → added/removed). */
  onModelChange(handler: ModelChangeHandler): () => void {
    return this.addHandler(this.modelChangeHandlers, handler);
  }

  /** Subscribe to semantic changes (UASemanticChangeEvent → rename/relocate). */
  onSemanticChange(handler: SemanticChangeHandler): () => void {
    return this.addHandler(this.semanticChangeHandlers, handler);
  }

  /**
   * Subscribe to metadata events (UAFunctionMetadata)
   * Receives: unit, unitDescription, euRange, valuePrecision
   * @returns Unsubscribe function
   */
  onMetadata(handler: MetadataHandler): () => void {
    return this.addHandler(this.metadataHandlers, handler);
  }

  /**
   * Subscribe to incident transitions (IncidentChanged).
   * Receives the browsePath + state change; typically used to trigger a
   * SWR mutate on the incidents endpoint.
   * @returns Unsubscribe function
   */
  onIncidentChanged(handler: IncidentChangedHandler): () => void {
    return this.addHandler(this.incidentChangedHandlers, handler);
  }

  /**
   * Get cached metadata by deviceId + functionName
   */
  getMetadata(
    deviceId: string,
    functionName: string
  ): UAFunctionMetadataMessage["metadata"] | undefined {
    return this.metadataCache.get(`${deviceId}/${functionName}`);
  }

  /**
   * Find metadata for a function by matching any cached entry
   * @deprecated Use getMetadata(namespace, nodeId) instead
   */
  getMetadataForFunction(
    _functionPath: string
  ): UAFunctionMetadataMessage["metadata"] | undefined {
    return undefined;
  }

  /**
   * Join a room to receive events for devices in that location
   * Uses reference counting - only actually joins on first subscription
   * @param roomId - The hierarchicalLocation (e.g., "Labor 1")
   */
  subscribe(roomId: string): void {
    const count = this.roomSubscriptions.get(roomId) || 0;
    this.roomSubscriptions.set(roomId, count + 1);

    // Only emit join on first subscription
    if (count === 0) {
      console.log(
        "[WS] Subscribing to room:",
        roomId,
        "connected:",
        this.isConnected
      );
      if (this.socket && this.isConnected) {
        this.socket.emit("join", roomId);
      }
    }
  }

  /**
   * Leave a room to stop receiving events
   * Uses reference counting - only actually leaves when last subscriber unsubscribes
   * @param roomId - The hierarchicalLocation
   */
  unsubscribe(roomId: string): void {
    const count = this.roomSubscriptions.get(roomId) || 0;
    if (count <= 1) {
      this.roomSubscriptions.delete(roomId);
      // Only emit leave when last subscriber leaves
      if (this.socket && this.isConnected) {
        this.socket.emit("leave", roomId);
      }
    } else {
      this.roomSubscriptions.set(roomId, count - 1);
    }
  }

  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }
}

// Singleton instance.
// Ohne expliziten Override verbinden wir uns auf den Origin, von dem die Seite
// geladen wurde (window.location.origin) — im Dev proxyt Vite "/socket.io" ans
// Backend, in Prod liefert Traefik es am selben Origin. Damit ist der konkrete
// Vite-Port (5173/5174/…) egal. VITE_LADS_WEBSOCKET_URL bleibt als Override.
const resolveWebSocketUrl = (): string =>
  import.meta.env.VITE_LADS_WEBSOCKET_URL ||
  (typeof window === "undefined"
    ? "http://localhost:3009"
    : window.location.origin);

export const ladsWebSocketService = new LADSWebSocketService({
  url: resolveWebSocketUrl(),
});

// Auto-connect on import (no-op in Storybook, siehe connect()).
// eslint-disable-next-line unicorn/prefer-top-level-await
ladsWebSocketService.connect().catch((error) => {
  console.error("[WS] Initial connection failed:", error);
});
